import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Copy, Loader2, Plus, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { PageHeader } from "@/components/ui/page-header";
import { useScope } from "@/hooks/use-scope";

import {
  listApiKeys,
  createApiKey,
  setApiKeyActive,
  deleteApiKey,
  apiKeyScopes,
} from "@/lib/api-keys.functions";

export const Route = createFileRoute("/_authenticated/app/admin/api-keys")({
  component: ApiKeysAdmin,
});

function ApiKeysAdmin() {
  const { tenantId } = useScope();
  const qc = useQueryClient();
  const listFn = useServerFn(listApiKeys);
  const createFn = useServerFn(createApiKey);
  const setActiveFn = useServerFn(setApiKeyActive);
  const deleteFn = useServerFn(deleteApiKey);

  const { data, isLoading } = useQuery({
    queryKey: ["api-keys", tenantId],
    queryFn: () => listFn({ data: { tenantId: tenantId! } }),
    enabled: !!tenantId,
  });

  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState<string[]>(["orders:write"]);
  const [reveal, setReveal] = useState<string | null>(null);

  const createMut = useMutation({
    mutationFn: () =>
      createFn({
        data: {
          tenantId: tenantId!,
          name,
          scopes: scopes as ("orders:write" | "orders:read")[],
        },
      }),
    onSuccess: (r) => {
      setReveal(r.fullKey);
      setName("");
      setOpen(false);
      qc.invalidateQueries({ queryKey: ["api-keys", tenantId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (!tenantId) {
    return <div className="p-6 text-sm text-muted-foreground">Select a tenant to manage API keys.</div>;
  }

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <PageHeader
        title="API Keys"
        description="Issue, scope, and revoke keys for the public REST API (POST /api/public/v1/orders)."
        actions={
          <Button onClick={() => setOpen(true)}>
            <Plus className="h-4 w-4 mr-1" /> New key
          </Button>
        }
      />

      <Card className="p-0 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : (data?.rows ?? []).length === 0 ? (
          <div className="p-6 text-sm text-muted-foreground">No keys yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left">
              <tr>
                <th className="px-4 py-2">Name</th>
                <th className="px-4 py-2">Prefix</th>
                <th className="px-4 py-2">Scopes</th>
                <th className="px-4 py-2">Last used</th>
                <th className="px-4 py-2">Active</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {(data?.rows ?? []).map((r: any) => (
                <tr key={r.id} className="border-t">
                  <td className="px-4 py-2 font-medium">{r.name}</td>
                  <td className="px-4 py-2 font-mono text-xs">lvbl_{r.key_prefix}…</td>
                  <td className="px-4 py-2">
                    <div className="flex flex-wrap gap-1">
                      {(r.scopes ?? []).map((s: string) => (
                        <Badge key={s} variant="secondary">{s}</Badge>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-2 text-muted-foreground">
                    {r.last_used_at ? new Date(r.last_used_at).toLocaleString() : "—"}
                  </td>
                  <td className="px-4 py-2">
                    <Switch
                      checked={r.is_active}
                      onCheckedChange={async (v) => {
                        await setActiveFn({ data: { id: r.id, active: v } });
                        qc.invalidateQueries({ queryKey: ["api-keys", tenantId] });
                      }}
                    />
                  </td>
                  <td className="px-4 py-2 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={async () => {
                        if (!confirm(`Permanently delete key "${r.name}"?`)) return;
                        await deleteFn({ data: { id: r.id } });
                        qc.invalidateQueries({ queryKey: ["api-keys", tenantId] });
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create API key</DialogTitle>
            <DialogDescription>
              The secret will be shown once. Copy it now and store it securely — we cannot retrieve it later.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="key-name">Name</Label>
              <Input id="key-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="ACME ERP" />
            </div>
            <div>
              <Label>Scopes</Label>
              <div className="flex flex-col gap-2 mt-2">
                {apiKeyScopes.map((s) => (
                  <label key={s} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={scopes.includes(s)}
                      onChange={(e) =>
                        setScopes((prev) =>
                          e.target.checked ? [...prev, s] : prev.filter((x) => x !== s),
                        )
                      }
                    />
                    <code>{s}</code>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button
              disabled={!name || scopes.length === 0 || createMut.isPending}
              onClick={() => createMut.mutate()}
            >
              {createMut.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!reveal} onOpenChange={(o) => !o && setReveal(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Your new API key</DialogTitle>
            <DialogDescription>
              This is the only time you'll see this secret. Copy it now.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-md bg-muted p-3 font-mono text-xs break-all">{reveal}</div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                if (reveal) navigator.clipboard.writeText(reveal);
                toast.success("Copied");
              }}
            >
              <Copy className="h-4 w-4 mr-1" /> Copy
            </Button>
            <Button onClick={() => setReveal(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}